import {useEffect, useRef} from 'react';
import type {ISeriesApi, ISeriesMarkersPluginApi} from 'lightweight-charts';
import {createSeriesMarkers, LineData, LineSeries, Time} from 'lightweight-charts';
import type {AlgorithmPatternRawData, TrendPatternRawData} from '../../../../types/patternRecognition';
import {
    resolveFibonacciLineLabel,
    resolvePatternLinePoints,
    resolvePatternOverlayLines,
    resolveTrendLineLabel,
} from '../utils/patternOverlayLines';

/**
 * 趋势形态结构线图层 Hook
 * 在图表上绘制趋势形态的结构线和锚点标记
 */
export function useTrendPatternOverlay(
    chart: ReturnType<typeof import('lightweight-charts').createChart> | null,
    candleSeries: ISeriesApi<'Candlestick'> | null,
    patternData: TrendPatternRawData | AlgorithmPatternRawData | null,
    visible: boolean,
    latestCandleTime?: number,
) {
    // 存储结构线 series 的引用
    const lineSeriesRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
    const lineMarkerPluginsRef = useRef<Map<string, ISeriesMarkersPluginApi<Time>>>(new Map());
    // 存储锚点 markers plugin
    const markerPluginRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);

    // 初始化 markers plugin
    useEffect(() => {
        if (!candleSeries) return;
        if (!markerPluginRef.current) {
            markerPluginRef.current = createSeriesMarkers(candleSeries, []);
        }
        return () => {
            if (markerPluginRef.current) {
                try {
                    markerPluginRef.current.setMarkers([]);
                } catch {
                    // 图表可能已随组件树卸载被销毁（Object is disposed），忽略
                }
                markerPluginRef.current = null;
            }
        };
    }, [candleSeries]);

    // 颜色配置
    const COLORS = {
        horizontal: '#4CAF50',
        segment: '#2196F3',
        breakout: '#FF4FA3',
        ray: '#FF9800',
        polyline: '#9C27B0',
        curve: '#E91E63',
        anchor: '#FFD700',
        bullish: '#4CAF50',
        bearish: '#F44336',
    };

    useEffect(() => {
        if (!chart || !candleSeries || !visible) {
            if (chart) {
                lineMarkerPluginsRef.current.forEach(plugin => {
                    try {
                        plugin.setMarkers([]);
                    } catch {
                        // 图表可能已随组件树卸载被销毁（Object is disposed），忽略
                    }
                });
                lineMarkerPluginsRef.current.clear();
                lineSeriesRef.current.forEach((series) => {
                    try {
                        chart.removeSeries(series);
                    } catch {
                        // series may have been removed
                    }
                });
                lineSeriesRef.current.clear();
            }
            return;
        }

        lineMarkerPluginsRef.current.forEach(plugin => {
            try {
                plugin.setMarkers([]);
            } catch {
                // 图表可能已随组件树卸载被销毁（Object is disposed），忽略
            }
        });
        lineMarkerPluginsRef.current.clear();
        lineSeriesRef.current.forEach((series) => {
            try {
                chart.removeSeries(series);
            } catch {
                // series may have been removed
            }
        });
        lineSeriesRef.current.clear();

        if (!patternData?.lines || patternData.lines.length === 0) {
            return;
        }

        resolvePatternOverlayLines(patternData).forEach((line) => {
            if (!line.points || line.points.length < 2) return;

            const resolvedPoints = resolvePatternLinePoints(patternData, line, latestCandleTime);
            const fibonacciLabel = resolveFibonacciLineLabel(patternData, line, latestCandleTime);
            const trendLineLabel = resolveTrendLineLabel(patternData, line, resolvedPoints);
            const displayPoints = fibonacciLabel
                ? [
                    resolvedPoints[0],
                    {time: fibonacciLabel.time, price: resolvedPoints[0].price},
                    resolvedPoints[resolvedPoints.length - 1],
                ]
                : trendLineLabel && resolvedPoints.length === 2
                    ? [
                        resolvedPoints[0],
                        {time: trendLineLabel.time, price: trendLineLabel.price},
                        resolvedPoints[1],
                    ]
                    : resolvedPoints;
            const lineData: LineData[] = displayPoints.map(p => ({
                time: (p.time / 1000) as Time,
                value: Number(p.price),
            }));

            let lineColor = COLORS.segment;
            let lineStyle = 0;

            switch (line.type) {
                case 'horizontal':
                    lineColor = COLORS.horizontal;
                    lineStyle = 0;
                    break;
                case 'segment':
                    lineColor = COLORS.segment;
                    lineStyle = 0;
                    break;
                case 'ray':
                    lineColor = COLORS.ray;
                    lineStyle = 2;
                    break;
                case 'polyline':
                    lineColor = COLORS.polyline;
                    lineStyle = 1;
                    break;
                case 'curve':
                    lineColor = COLORS.curve;
                    lineStyle = 0;
                    break;
            }
            if (fibonacciLabel) {
                lineColor = fibonacciLabel.touched ? '#FFD166' : '#90CAF9';
            } else if (line.key === 'breakout') {
                lineColor = COLORS.breakout;
                lineStyle = 2;
            }

            const lineSeries = chart.addSeries(LineSeries, {
                color: lineColor,
                lineWidth: 2,
                lineStyle: lineStyle,
                crosshairMarkerVisible: false,
                lastValueVisible: false,
                priceLineVisible: false,
            });

            lineSeries.setData(lineData);
            lineSeriesRef.current.set(line.key, lineSeries);
            if (fibonacciLabel || trendLineLabel) {
                const markerTime = fibonacciLabel
                    ? fibonacciLabel.time : trendLineLabel!.time;
                const markerPlugin = createSeriesMarkers(lineSeries, [{
                    time: (markerTime / 1000) as Time,
                    position: 'inBar',
                    color: fibonacciLabel
                        ? (fibonacciLabel.touched ? '#FFD166' : '#90CAF9')
                        : lineColor,
                    shape: 'circle',
                    text: fibonacciLabel?.text ?? trendLineLabel?.text ?? '',
                }]);
                lineMarkerPluginsRef.current.set(line.key, markerPlugin);
            }
        });

    }, [chart, candleSeries, patternData, visible, latestCandleTime]);

    // 绘制锚点标记
    useEffect(() => {
        const m = markerPluginRef.current;
        if (!m || !visible || !patternData?.anchors) {
            if (m) m.setMarkers([]);
            return;
        }

        const markers = [...patternData.anchors]
            .sort((a, b) => a.time - b.time)
            .map(anchor => {
                let color = COLORS.anchor;
                let shape: 'circle' | 'arrowDown' | 'arrowUp' | 'square' = 'circle';
                let position: 'aboveBar' | 'belowBar' = 'aboveBar';

                const label = anchor.label?.toUpperCase() || '';

                if (label.includes('NECKLINE') || label.includes('TROUGH')) {
                    color = '#FF9800';
                    shape = 'square';
                    position = (patternData as TrendPatternRawData).breakoutDirection > 0 ? 'aboveBar' : 'belowBar';
                } else if ((patternData as TrendPatternRawData).breakoutDirection < 0
                    && (label.includes('HEAD') || label.includes('SHOULDER') || label.includes('PEAK'))) {
                    color = '#F44336';
                    shape = 'arrowDown';
                } else if ((patternData as TrendPatternRawData).breakoutDirection > 0
                    && (label.includes('HEAD') || label.includes('SHOULDER'))) {
                    color = '#4CAF50';
                    shape = 'arrowUp';
                    position = 'belowBar';
                }

                return {
                    time: (anchor.time / 1000) as Time,
                    position,
                    color: color,
                    shape: shape,
                    text: anchor.label || '',
                };
            });

        m.setMarkers(markers);

        return () => {
            if (markerPluginRef.current) {
                markerPluginRef.current.setMarkers([]);
            }
        };
    }, [candleSeries, patternData?.anchors, visible]);

    // 清理函数
    useEffect(() => {
        return () => {
            if (chart) {
                lineMarkerPluginsRef.current.forEach(plugin => plugin.setMarkers([]));
                lineMarkerPluginsRef.current.clear();
                lineSeriesRef.current.forEach((series) => {
                    try {
                        chart.removeSeries(series);
                    } catch {
                        // ignore
                    }
                });
                lineSeriesRef.current.clear();
            }
        };
    }, [chart]);

    return {
        clearLines: () => {
            if (chart) {
                lineMarkerPluginsRef.current.forEach(plugin => plugin.setMarkers([]));
                lineMarkerPluginsRef.current.clear();
                lineSeriesRef.current.forEach((series) => {
                    try {
                        chart.removeSeries(series);
                    } catch {
                        // ignore
                    }
                });
                lineSeriesRef.current.clear();
            }
            if (markerPluginRef.current) {
                markerPluginRef.current.setMarkers([]);
            }
        },
    };
}
